import { asAddDocData } from '@services/firebase/firestore/collectionHandler';
import { QueryConstraints } from '@services/firebase/firestore/firestore';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { InitialDataService } from '@services/loadInitialData';
import { accountStore } from '@stores/bootstrap-stores/account';
import { log } from '@utils/log';
import { mergeRecordsById } from '@utils/mergeRecordsById';
import { Form, FormIntent, InAppForm, TableForm } from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';

export const formsDataGridId = 'forms-data-grid';
export const tagsGridId = 'tags-grid';
export const categoriesGridId = 'categories-grid';

function formsFactory() {
  async function getForm<T = Form>(_caller: string, id: string) {
    return collectionsService.forms.getDoc<T>(id);
  }

  async function getFormsByIds<T = Form>(
    caller: string,
    ids: string[],
    queryConstraints?: QueryConstraints,
    onProgress?: (forms: T[]) => void
  ) {
    log.info(`[formsFactory][${caller}] Fetching forms by ids`, { ids });
    if (!ids.length) {
      return [];
    }

    const forms = await collectionsService.forms.getDocsInBatches(
      ['id', 'in', ids],
      queryConstraints,
      (batchForms) => onProgress?.(batchForms as T[])
    );
    return forms.flat() as T[];
  }

  async function getForms(_caller: string, onProgress?: (forms: Form[]) => void) {
    const { scopeQuery, scope, resourceGrants } = accountStore.getState();

    let formsStore: Form[] = [];
    const emitForms = (nextForms: Form[]) => onProgress?.(nextForms);

    if (['tenant', 'group'].includes(scope)) {
      await InitialDataService.load<Form>('forms', [scopeQuery], {}, (forms) => {
        formsStore = forms;
        emitForms(forms);
      });
      return formsStore;
    }

    // only load the form groups and forms that the non-tenant has access to, which are determined by the plans
    const formGrants = resourceGrants?.forms;

    const formIds = formGrants?.map((f) => f.resource_id);
    if (!formIds?.length) {
      emitForms([]);
      return [];
    }

    let progressiveForms: Form[] = [];
    const forms = await collectionsService.forms.getDocsInBatches(
      ['id', 'in', formIds],
      undefined,
      (batchForms) => {
        progressiveForms = mergeRecordsById(progressiveForms, batchForms as Form[]);
        emitForms(progressiveForms);
      }
    );

    const resolvedForms = forms.flat();
    emitForms(resolvedForms);
    return resolvedForms;
  }

  async function getFormsByIntent(formIntent: FormIntent[], onProgress?: (forms: Form[]) => void) {
    const forms = await collectionsService.forms.getDocsInBatches(
      ['intent', 'in', formIntent],
      undefined,
      (batchForms) => onProgress?.(batchForms as Form[])
    );
    return forms.flat();
  }

  async function updateForm(id: string, data: Partial<Form>) {
    return collectionsService.forms.updateDoc(id, data);
  }

  async function addForm(data: Partial<Form> & { id: string }) {
    return collectionsService.forms.addDoc(data.id, asAddDocData(data));
  }

  async function getLinkedTff(linkedFormId: string) {
    return collectionsService.forms.getDocs<TableForm>(
      where('options.linked_table_id', '==', linkedFormId)
    );
  }

  async function getInAppForms(slots: InAppForm['slot'][]) {
    return collectionsService.forms.getDocs<InAppForm>([
      where('intent', '==', 'in-app'),
      where('slot', 'in', slots)
    ]);
  }

  return {
    getForm,
    getForms,
    getFormsByIds,
    getFormsByIntent,
    updateForm,
    addForm,
    getLinkedTff,
    getInAppForms
  };
}

export const formsService = formsFactory();
